import { Fragment, useEffect, useRef, useState } from "react";
import { useDocumentCountSafe } from "@/features/nbi/context/DocumentCountContext";
import { useNbiFormContextSafe } from "@/features/nbi/context/NbiFormContext";
import type { LoanApplicationData } from "@/features/nbi/hooks/useNbiForm";
import { getFieldsValidation, MenuBagdeConfigs } from "@/features/nbi/utils/documentValidationMessages";
import { getApplicantsWithoutIncome, hasVisitedIncomePage } from "@/features/nbi/utils/incomeValidationUtils";

const STORAGE_KEY = "nbi-document-field-mappings";

const ErrorAndWarningBadge = ({
    errors,
    warnings,
    step,
    currentStep,
    isCollapsed,
}: {
    errors: number;
    warnings: number;
    step: number;
    currentStep: number;
    isCollapsed: boolean;
}) => {
    if (currentStep !== step) return <></>;
    if (errors) return <BadgeError variant="error" number={errors} isCollapsed={isCollapsed} />;
    if (warnings) return <BadgeError variant="warning" number={warnings} isCollapsed={isCollapsed} />;
    return <></>;
};

export const ErrorAndWarningBadges = ({
    currentStep: menuStep = 0,
    isCollapsed = false,
}: {
    currentStep?: number;
    isCollapsed?: boolean;
}) => {
    const [, setUpdateTrigger] = useState(0);
    const lastStorageValueRef = useRef<string | null>(null);
    const formContext = useNbiFormContextSafe();
    const documentCount = useDocumentCountSafe();

    const allDocumentsProcessed = documentCount
        ? documentCount.completedCount === documentCount.totalCount && documentCount.totalCount > 0
        : false;

    useEffect(() => {
        const checkForChanges = () => {
            const currentValue = sessionStorage.getItem(STORAGE_KEY);
            if (currentValue !== lastStorageValueRef.current) {
                lastStorageValueRef.current = currentValue;
                setUpdateTrigger((prev) => prev + 1);
            }
        };

        checkForChanges();

        const interval = setInterval(checkForChanges, 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (!formContext?.form?.store) return;

        const unsubscribe = formContext.form.store.subscribe(() => {
            setUpdateTrigger((prev) => prev + 1);
        });

        return unsubscribe;
    }, [formContext?.form?.store]);

    const formValues = formContext?.form?.state?.values as LoanApplicationData | undefined;
    const applicants = formValues?.applicants || [];
    const incomes = formValues?.incomes || [];
    const applicationId = formValues?.applicationId;

    const applicantsWithoutIncome = getApplicantsWithoutIncome(applicants, incomes);
    const applicantsWithoutIncomeIds = applicantsWithoutIncome.map((a) => a.id);

    const showMissingIncomeBadge =
        hasVisitedIncomePage(applicationId, applicantsWithoutIncomeIds) && applicantsWithoutIncome.length > 0;

    if (!allDocumentsProcessed) {
        if (!showMissingIncomeBadge) {
            return null;
        }
        if (menuStep !== 7) {
            return null;
        }
        return (
            <ErrorAndWarningBadge
                errors={0}
                warnings={1}
                step={menuStep}
                currentStep={menuStep}
                isCollapsed={isCollapsed}
            />
        );
    }

    const badges = MenuBagdeConfigs.map((config) => {
        let errorCount = 0;
        let warningCount = 0;
        config.elements(formContext?.form).forEach((element: any, _: number) => {
            const fieldValidation = getFieldsValidation(element);
            errorCount += fieldValidation.errors;
            warningCount += fieldValidation.warnings;
        });

        const isMissingIncomeStep = config.step === 7;
        const shouldShowMissingIncome = isMissingIncomeStep && showMissingIncomeBadge;
        const additionalWarnings = shouldShowMissingIncome ? 1 : 0;

        return (
            <Fragment key={config.field}>
                <ErrorAndWarningBadge
                    errors={errorCount}
                    warnings={warningCount + additionalWarnings}
                    step={config.step}
                    currentStep={menuStep}
                    isCollapsed={isCollapsed}
                />
            </Fragment>
        );
    });

    return <>{badges}</>;
};

export const BadgeError = ({
    isCollapsed,
    number,
    variant,
}: {
    isCollapsed?: boolean;
    number: number;
    variant: "error" | "warning";
}) => {
    const className = `ml-auto hidden h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-medium leading-none self-center text-white ${!isCollapsed ? "md:flex" : ""}`;
    const badgeBgClass = variant === "error" ? "bg-red-800" : "bg-yellow-700";
    return (
        <span className={`${className} ${badgeBgClass}`} aria-label={`${number} notifications`}>
            {number}
        </span>
    );
};
